import * as React from 'react';
/**
 * Generic polling hook that executes async callback function every `interval` milliseconds and has a shouldCancelRequest parameter that can be used to cancel the polling.
 * It returns a generic response of the executed callback function.
 *
 * @param callback - async function to be executed
 * @param shouldCancelRequest - boolean to cancel the polling
 * @param interval - interval in milliseconds, default is 1000
 *
 * @returns response, tries - response is the generic response of the executed callback function and tries is the number of times the callback function was executed
 */
export const usePoll = <T>(
  callback: (abortController: AbortController) => Promise<T>,
  shouldCancelRequest: boolean,
  interval: number = 1000,
) => {
  const [response, setResponse] = React.useState<T | undefined>(undefined);
  const [tries, setTries] = React.useState(0);

  React.useEffect(() => {
    if (shouldCancelRequest) {
      return;
    }

    const abortController = new AbortController();
    const runPoll = async () => {
      try {
        const res = await callback(abortController);
        setResponse(res);
        setTries((prev) => prev + 1);
      } catch (error) {
        if (!abortController.signal.aborted) {
          throw Error(`Could not poll: ${error}`);
        }
      }
    };

    const intervalId = setInterval(() => {
      void runPoll();
    }, interval);

    return () => {
      clearInterval(intervalId);
      abortController.abort();
    };
  }, [callback, shouldCancelRequest, interval]);

  return {response, tries} as const;
};
